/**
 * Execution Logs Viewer
 * Displays detailed logs from integration execution
 */

import React, { useState } from 'react';
import {
  FileText,
  AlertTriangle,
  Info,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { IntegrationLog, getLogLevelColor, LogLevel } from '@/services/api/integrations';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface Props {
  logs: IntegrationLog[];
  onRefresh: () => void;
}

export const ExecutionLogsViewer: React.FC<Props> = ({ logs, onRefresh }) => {
  const { t } = useAppTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<LogLevel | 'all'>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm ||
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.details && JSON.stringify(log.details).toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesLevel = selectedLevel === 'all' || log.log_level === selectedLevel;

    const matchesType = selectedType === 'all' || log.log_type === selectedType;

    return matchesSearch && matchesLevel && matchesType;
  });

  // Get unique log types
  const logTypes = Array.from(new Set(logs.map(l => l.log_type).filter(Boolean)));

  // Count by level
  const levelCounts = {
    DEBUG: logs.filter(l => l.log_level === 'DEBUG').length,
    INFO: logs.filter(l => l.log_level === 'INFO').length,
    WARNING: logs.filter(l => l.log_level === 'WARNING').length,
    ERROR: logs.filter(l => l.log_level === 'ERROR').length,
    CRITICAL: logs.filter(l => l.log_level === 'CRITICAL').length,
  };

  const levelIcons = {
    DEBUG: <Info className="h-4 w-4 text-gray-500" />,
    INFO: <Info className="h-4 w-4 text-blue-600" />,
    WARNING: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    ERROR: <XCircle className="h-4 w-4 text-red-600" />,
    CRITICAL: <AlertCircle className="h-4 w-4 text-red-700" />,
  };

  // Export logs as JSON
  const handleExport = () => {
    const dataStr = JSON.stringify(filteredLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `logs_${new Date().toISOString()}.json`;
    link.click();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            {t('integrations.execution_logs', 'Execution Logs')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
            {filteredLogs.length !== logs.length && ` (filtered from ${logs.length})`}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('integrations.refresh', 'Refresh')}
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t('integrations.export', 'Export')}
          </button>
        </div>
      </div>

      {/* Level Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        <FilterButton
          label={t('integrations.all', 'All')}
          count={logs.length}
          active={selectedLevel === 'all'}
          onClick={() => setSelectedLevel('all')}
        />
        {(Object.keys(levelCounts) as LogLevel[]).map(level => (
          <FilterButton
            key={level}
            label={level}
            count={levelCounts[level]}
            active={selectedLevel === level}
            onClick={() => setSelectedLevel(level)}
            color={level}
          />
        ))}
      </div>

      {/* Search and Type Filter */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('integrations.search_logs', 'Search logs...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {logTypes.length > 0 && (
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">{t('integrations.all_types', 'All Types')}</option>
            {logTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        )}
      </div>

      {/* Logs List */}
      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">{t('integrations.no_logs_found', 'No logs found')}</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {filteredLogs.map(log => (
            <LogEntry key={log.id} log={log} icon={levelIcons[log.log_level]} />
          ))}
        </div>
      )}
    </div>
  );
};

// Filter Button
interface FilterButtonProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  color?: LogLevel;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, count, active, onClick, color }) => {
  const getColorClass = () => {
    if (!color) return active ? 'bg-teal-100 text-teal-700 border-teal-300' : 'bg-white text-gray-600 border-gray-300 dark:border-gray-600';

    const colors = {
      DEBUG: active ? 'bg-gray-100 text-gray-700 border-gray-300 dark:border-gray-600' : 'bg-white text-gray-600 border-gray-200',
      INFO: active ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-blue-600 border-blue-200',
      WARNING: active ? 'bg-yellow-100 text-yellow-700 border-yellow-300' : 'bg-white text-yellow-600 border-yellow-200',
      ERROR: active ? 'bg-red-100 text-red-700 border-red-300' : 'bg-white text-red-600 border-red-200',
      CRITICAL: active ? 'bg-red-200 text-red-800 border-red-400' : 'bg-white text-red-700 border-red-300',
    };

    return colors[color];
  };

  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-lg border text-sm font-medium whitespace-nowrap transition-colors ${getColorClass()}`}
    >
      {label}
      <span className="ml-2 px-1.5 py-0.5 bg-white bg-opacity-50 rounded text-xs">
        {count}
      </span>
    </button>
  );
};

// Log Entry
interface LogEntryProps {
  log: IntegrationLog;
  icon: React.ReactNode;
}

const LogEntry: React.FC<LogEntryProps> = ({ log, icon }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:bg-gray-700 transition-colors"
      >
        <div className="mt-0.5">{icon}</div>

        <div className="flex-1 text-left">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getLogLevelColor(log.log_level)}`}>
              {log.log_level}
            </span>
            {log.log_type && (
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                {log.log_type}
              </span>
            )}
            {log.step_name && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Step: {log.step_name}
              </span>
            )}
          </div>

          <p className="text-sm text-gray-900 dark:text-white">{log.message}</p>

          <p className="text-xs text-gray-500 mt-1">
            {fmt.dateTime(log.logged_at)}
            {log.record_id && ` • Record: ${log.record_id}`}
            {log.internal_id && ` • ID: ${log.internal_id}`}
          </p>
        </div>
      </button>

      {expanded && (log.details || log.stack_trace || log.error_code) && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 space-y-3">
          {log.details && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">{t('integrations.details', 'Details')}</p>
              <pre className="bg-white dark:bg-gray-800 rounded p-2 text-xs overflow-x-auto border border-gray-200 dark:border-gray-700 dark:border-gray-700">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          )}

          {log.error_code && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">{t('integrations.error_code', 'Error Code')}</p>
              <p className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200 dark:border-red-700">
                {log.error_code}
              </p>
            </div>
          )}

          {log.stack_trace && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">{t('integrations.stack_trace', 'Stack Trace')}</p>
              <pre className="bg-white dark:bg-gray-800 rounded p-2 text-xs overflow-x-auto border border-red-200 dark:border-red-700 text-red-600">
                {log.stack_trace}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
